/* eslint-disable react-hooks/exhaustive-deps */
import { Fragment, createElement, useEffect, useRef } from "react"
import type { FC, ReactElement, RefObject } from "react"

import type { AxiosResponse } from "axios"
import forEach from "lodash-es/forEach"
import { useBoolean, useInterval, useUpdateEffect } from "react-use"

import { axiosClient } from "$/http"
import {
    getCurrentAccessToken,
    getCurrentRefreshToken,
    isJwtTokenLive,
    isJwtTokenValid,
    setCurrentAccessToken,
    setCurrentRefreshToken
} from "$/utils/tokens"

import { type TLogoutFunction, type TUserState, useIsFakeParticipant, useLogout, useUser } from "@/3514/hooks"
import { type IUserParticipantModel } from "@/3514/types"
import { isUserParticipant, seconds } from "@/3514/utils"
import Urls from "@/api/urls"
import { MAX_JS_TIMEOUT_VALUE_IN_MS } from "@/constants"
import type { IJwtTokenPairDto } from "@/models/jwt"
import { EStorageServiceType, LocalStorageServiceKeys, StorageService } from "@/services"
import { type TEmptyCallback, emptyCallback } from "@/shared/types/functions"

const localStorageService: StorageService = new StorageService(EStorageServiceType.Local)
const sessionStorageService: StorageService = new StorageService(EStorageServiceType.Session)

function logoutFakeParticipant(): void {
    sessionStorageService.clearStorage()
    window.location.reload()
}

const WINDOW_ACTIVITY_EVENTS: string[] = ["mousemove", "mousedown", "keydown", "touchstart", "wheel", "click"]
const DOCUMENT_ACTIVITY_EVENTS: string[] = ["scroll"]

function useActivityTimeout(timeoutMs: number | null, onActivity?: TEmptyCallback): boolean {
    const [isTimedOut, setIsTimedOut]: ReturnType<typeof useBoolean> = useBoolean(false)

    const timeoutIdRef: RefObject<ReturnType<typeof setTimeout>> = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect((): TEmptyCallback => {
        if (timeoutMs === null) {
            setIsTimedOut(false)
            return emptyCallback
        }

        let lastActivityTime: number = 0
        const ACTIVITY_THROTTLE_MS: number = 500

        const resetTimer: TEmptyCallback = (): void => {
            if (timeoutIdRef.current !== null) clearTimeout(timeoutIdRef.current)

            setIsTimedOut(false)
            timeoutIdRef.current = setTimeout((): void => setIsTimedOut(true), timeoutMs)

            const now: number = Date.now()
            if (now - lastActivityTime > ACTIVITY_THROTTLE_MS) {
                lastActivityTime = now
                onActivity?.()
            }
        }

        resetTimer()

        forEach<string>(
            WINDOW_ACTIVITY_EVENTS,
            (event: string): void => void window.addEventListener(event, resetTimer, { passive: true })
        )
        forEach<string>(
            DOCUMENT_ACTIVITY_EVENTS,
            (event: string): void => void document.addEventListener(event, resetTimer, { passive: true })
        )

        return (): void => {
            if (timeoutIdRef.current !== null) clearTimeout(timeoutIdRef.current)

            forEach<string>(
                WINDOW_ACTIVITY_EVENTS,
                (event: string): void => void window.removeEventListener(event, resetTimer)
            )
            forEach<string>(
                DOCUMENT_ACTIVITY_EVENTS,
                (event: string): void => void document.removeEventListener(event, resetTimer)
            )
        }
    }, [timeoutMs, onActivity])

    return isTimedOut
}

const Guard: FC = (): ReactElement => {
    const { isLoaded: isUserLoaded, user }: TUserState = useUser()

    const isParticipant: boolean = isUserParticipant(user)

    const logout: TLogoutFunction = useLogout()

    const isFakeParticipant: boolean = useIsFakeParticipant()

    const doesUserHaveBrowserSessionTtl: boolean = isParticipant
        ? Number.isFinite((user as IUserParticipantModel).browserSessionTtl)
        : false

    const participantIdleTime: number | null = ((): number | null => {
        if (!isUserLoaded || !isParticipant || !doesUserHaveBrowserSessionTtl) return null

        const ttlMs: number = Number((user as IUserParticipantModel).browserSessionTtl) * 1000

        return ttlMs > MAX_JS_TIMEOUT_VALUE_IN_MS ? MAX_JS_TIMEOUT_VALUE_IN_MS : ttlMs
    })()

    const lastTokenRefreshRef: RefObject<number> = useRef<number>(0)
    const isTokenRefreshInFlightRef: RefObject<boolean> = useRef<boolean>(false)

    const handleActivity: TEmptyCallback = (): void => {
        if (!isParticipant || !doesUserHaveBrowserSessionTtl) return
        if (isTokenRefreshInFlightRef.current) return

        const now: number = Date.now()

        if (now - lastTokenRefreshRef.current < seconds(5)) return

        lastTokenRefreshRef.current = now

        const token: string = getCurrentAccessToken()

        if (!(isJwtTokenValid(token) && isJwtTokenLive(token, seconds(10)))) {
            isTokenRefreshInFlightRef.current = true

            void axiosClient
                .post(Urls.postTokenPairRefresh(), { refresh: getCurrentRefreshToken() })
                .then((response: AxiosResponse<IJwtTokenPairDto>): void => {
                    setCurrentAccessToken(response.data.access)
                    setCurrentRefreshToken(response.data.refresh)
                })
                .catch((): void => void 0)
                .finally((): void => {
                    isTokenRefreshInFlightRef.current = false
                })
        }
    }

    const isParticipantTimedOut: boolean = useActivityTimeout(participantIdleTime, handleActivity)

    useInterval(
        (): void => {
            if (!isFakeParticipant) return

            if (localStorageService.getItem<boolean>(LocalStorageServiceKeys.MainUserLoggedOut()))
                logoutFakeParticipant()
        },
        isFakeParticipant ? seconds(3) : null
    )

    useUpdateEffect((): void => {
        if (isUserLoaded)
            if (isParticipant && doesUserHaveBrowserSessionTtl && isParticipantTimedOut) {
                if (isFakeParticipant) return logoutFakeParticipant()

                void logout()
            }

        return void 0
    }, [isUserLoaded, isParticipant, isFakeParticipant, doesUserHaveBrowserSessionTtl, isParticipantTimedOut])

    return createElement(Fragment)
}

Guard.displayName = "ActivityGuard"

export { Guard as ActivityGuard }
